// ============================================================
// IssuedStatement.tsx — (B4, batch 2 read-first) an ISSUED settlement
// statement: a read-only summary card (number / version, status, issued by /
// when, signed by, who it's addressed to, the net + where it goes, delivery,
// fingerprint) with View PDF / Send… / Correct; then the court steps (child /
// protected party) or the client's answer (adults — a button opens the form,
// once recorded a readable summary + "Change answer"); then the B4b hand-off.
// Also the version list.
// (B4c batch 1b) The online-signing status (statement/EsignStatus): signed →
// "Signed online by … — View signed PDF · Evidence pack"; open → sent /
// opened / code verified with Resend link + Cancel request.
// (E-sign fixes B) The e-sign status shows even with the pilot off (no
// buttons that would fail — it says why), with the last e-sign refusal under
// it. The figure tiles wrap by the room they have (FIGURE_GRID): at 360 px /
// 200% zoom the Net sum tile gets a full row — no clipped £ amounts.
// (D12) A paper request opens the Send window on Posted or handed over; "not
// me" offers a new request; the redacted evidence pack sits beside the pack.
// The card carries data-statement-id (the bell's statement links scroll to it).
// (Big-audit fix batch 1) A request fully signed on an earlier version shows
// here too: "Signed on an earlier version (version N) — on <date>", with its
// signed PDF and evidence packs.
// (Settlement redesign) This is now the CURRENT STEP: the e-signature status, the
// client's answer or the court steps, the hand-off and the one main button. The
// details fold to one line; versions, earlier signings and the answer history
// live in the one History area (StatementPanel).
// ============================================================
import React, { useState } from 'react'
import type { recordClientApprovalApi } from '../../../api/casevoApi'
import {
  netSumLabel,
  type CaseStatements, type RepresentativeType, type StatementSummary,
} from '../../../types/settlementStatement.types'
import { B_PRIMARY, B_SECONDARY, B_SECONDARY_SM, NOTE, money } from '../../billing/billingUi'
import ActionBox from '../../shared/ActionBox'
import { ukDate } from '../../../utils/civilDate'
import { CHIP, FIGURE_GRID, Fold, StatusChip, StepTrail, SummaryRow, shortHash, stRef } from './statementBits'
import { answerMethodFor, type NextStep } from './nextStep'
import { CourtSteps, type RecordStage } from './CourtApproval'
import EsignStatus, { isEsignInProgress, isFullySigned } from './EsignStatus'
import { ClientAnswer, inSlot, type LaterActions, type MainSlot } from './ClientAnswer'
import type { StatementEsignSummary } from '../../../types/esign.types'

// (B4c 1b) What the statement card can do about an e-signature request.
export interface EsignActions {
  resend: () => void
  cancel: (reason: string) => Promise<boolean>
  viewSigned: () => void
  evidencePack: () => void
  // (D12)
  redactedPack?: () => void
  sendPaper?: () => void
  sendNew?: () => void
}

type ApprovalBody = Parameters<typeof recordClientApprovalApi>[2]
const DELIVERY: Record<'EMAIL' | 'POST' | 'HAND', string> = { EMAIL: 'Emailed', POST: 'Posted', HAND: 'Handed over' }

// Who the statement goes to: the litigation friend for a child / protected party.
export const addresseeOf = (s: StatementSummary, data: Pick<CaseStatements, 'litigationFriendName'>): string | null =>
  s.addressee?.name ?? (s.capacity !== 'ADULT' && data.litigationFriendName ? `${data.litigationFriendName} (litigation friend)` : null)

// (Big-audit fix batch 2) A request fully signed on an earlier version.
export interface EarlierSigned { version: number; e: StatementEsignSummary; actions: EsignActions; busy: string | null; error: string | null }
const QUIET = 'text-[13px] font-semibold text-casevo-link focus:outline-none casevo-focus rounded-[4px] hover:underline underline-offset-2 disabled:opacity-50'

export const IssuedCard: React.FC<{
  s: StatementSummary; data: CaseStatements; busy: string | null; hasDraft: boolean
  preview: () => void; send: () => void; correct: () => Promise<boolean>
  approve: (body: ApprovalBody) => Promise<boolean>
  // (Big-audit batch 2, M1) After an e-signed answer: a later instruction (record / approve / refuse).
  later?: LaterActions
  caseId?: string
  recordStage: RecordStage
  esignActions?: EsignActions
  // (E-sign fixes B) The firm's pilot is off; the last e-sign action's refusal.
  esignPilotOff?: boolean
  esignError?: string | null
  // (Big-audit fix batch 1) The busy key as the e-sign status should see it
  // (another request's action → disabled, not "busy").
  esignBusy?: string | null
  // (Settlement redesign) The sentence + main button, worked out once by the panel.
  step: NextStep
  // "Record the court order": the final statement's draft, its order form open.
  startFinal?: () => void
  // The page's sticky action bar. A card folded under a newer draft keeps its own row.
  barSlot?: MainSlot
}> = ({ s, data, busy, hasDraft, preview, send, correct, approve, later, caseId, recordStage, esignActions, esignPilotOff = false, esignError = null, esignBusy, step, startFinal, barSlot }) => {
  const childOrPp = s.capacity !== 'ADULT'
  const esign = data.esign?.[s.id] ?? null
  const to = addresseeOf(s, data)
  const action = step.main?.action ?? 'none'
  const signing = isEsignInProgress(esign)
  const handOff = <HandOff s={s} representativeType={data.representativeType} />
  // "Not ready yet" only once there's something to report: until the client has
  // answered (or a court statement has gone out), the step in hand says it all.
  const handOffTurn = s.readiness.ready || (childOrPp ? !!s.delivered : !!s.clientResponse)
  // Where the one main button goes, whichever part of the card it belongs to. With
  // a newer draft open, the draft has it: this card's buttons are all secondary.
  const [mainSlot, setMainSlot] = useState<HTMLElement | null>(null)
  const mainClass = hasDraft ? B_SECONDARY : B_PRIMARY
  const mainData = (a: string): string | undefined => (hasDraft ? undefined : a)
  const dispute = s.clientResponse?.response === 'DISPUTED' ? s.clientResponse : null
  const esignBox = esign && esignActions && (
    <EsignStatus e={esign} busy={esignBusy === undefined ? busy : esignBusy} onResend={esignActions.resend} onCancel={esignActions.cancel} onViewSigned={esignActions.viewSigned} onEvidencePack={esignActions.evidencePack}
      onRedactedPack={esignActions.redactedPack} onSendPaper={esignActions.sendPaper} onSendNew={esignActions.sendNew}
      pilotOff={esignPilotOff} error={esignError} />
  )
  return (
    <section aria-label={`Statement ${stRef(s)}`} data-statement-id={s.id} tabIndex={-1} className="scroll-mt-[16px] focus:outline-none flex flex-col gap-4">
      <div className="flex items-center gap-[10px] flex-wrap">
        <h3 className="m-0 text-[16px] font-semibold text-casevo-forest tabular-nums">{stRef(s)}</h3>
        <StatusChip status={s.status} />
        {s.proposed && <span className={`${CHIP} border-casevo-border bg-casevo-input text-casevo-text-soft`}>Proposed</span>}
      </div>
      {s.proposed && <p className={`${NOTE} m-0`}>This is a PROPOSED statement for the court’s approval. Once the order is recorded, start the final statement — it replaces this one.</p>}
      {s.readiness.ready && !dispute && handOff}
      {!dispute && esignBox}
      {dispute && (
        <div data-dispute-note="">
          <ActionBox ariaLabel="The dispute" title={`Disputed ${money(dispute.disputedPence ?? 0)}${dispute.date ? ` on ${ukDate(dispute.date)}` : ''}`}
            message={<>{dispute.comment ? <span className="block">“{dispute.comment}”</span> : null}<span className="block">{dispute.esignLocked ? 'If they’ve changed their mind, record a later instruction. If the statement is wrong, correct it: the dispute carries to the new version.' : 'When it’s settled, change the answer. If the statement is wrong, correct it.'}</span></>} />
        </div>
      )}
      {s.readiness.ready && dispute && handOff}
      {dispute && (isFullySigned(esign)
        ? <Fold className="rounded-[10px] border border-casevo-border-soft px-[12px] py-[8px]" labelClass="text-[13px] text-casevo-forest" dataKey="esign-signed" label={<><span className="text-casevo-brand dark:text-casevo-link">✓</span> Signed online · PDF and evidence</>}>{esignBox}</Fold>
        : esignBox)}
      {childOrPp
        ? <CourtSteps stages={data.courtStages} history={s.courtStageHistory} current={s.courtStage} busy={busy} today={data.today} onRecord={recordStage} />
        : <ClientAnswer s={s} today={data.today} busy={busy} onSubmit={approve} onRecordDelivery={send}
            caseId={caseId} later={later} viewerUserId={data.viewerUserId} canCheck={data.canCheck} onlyApprover={data.onlyApprover} checksOff={data.twoPersonChecks === false} noApprover={data.noApprover}
            openAtStep={!signing && !isFullySigned(esign)} defaultMethod={answerMethodFor(s.delivered)} historyElsewhere mainLater={action === 'later'} mainChange={action === 'change'} foldSummary mainSlot={mainSlot} quiet={hasDraft} />}
      {!s.readiness.ready && handOffTurn && handOff}
      {!s.pdfSaved && <p className="m-0 text-[12px] text-casevo-text-soft">The PDF wasn’t saved to the case yet — it’s made when you view or send it.</p>}
      {/* The one main button, the quiet actions beside it, the trail under it: the action bar. */}
      {inSlot(hasDraft ? undefined : barSlot, <div className="flex flex-col gap-[8px]" data-main-area="">
        <div className="flex flex-wrap-reverse items-center gap-x-[12px] gap-y-[4px]" data-main-row="">
          <span ref={setMainSlot} className="contents" data-main-slot="" />
          {action === 'send' && <button type="button" className={mainClass} data-main-action={mainData('send')} disabled={!!busy} onClick={send}>{step.main?.label}</button>}
          {action === 'finalStatement' && <button type="button" className={mainClass} data-main-action={mainData('finalStatement')} disabled={!!busy || hasDraft} onClick={startFinal ?? (() => { void correct() })}>{step.main?.label}</button>}
          <button type="button" className={QUIET} disabled={!!busy} onClick={preview}>View PDF</button>
          {action !== 'send' && <button type="button" className={QUIET} disabled={!!busy} onClick={send}>Send…</button>}
          {action !== 'finalStatement' && <button type="button" className={QUIET} disabled={!!busy || hasDraft} onClick={() => { void correct() }}>{s.proposed ? 'Start the final statement' : 'Correct (new version)'}</button>}
        </div>
        {hasDraft && <p className={`${NOTE} m-0`}>Finish the draft above first.</p>}
        <StepTrail trail={step.trail} />
      </div>)}
      <Fold className="rounded-[10px] border border-casevo-border-soft px-[12px] py-[8px]" labelClass="text-[13px] text-casevo-forest" dataKey="statement-look"
        label={<><span className="text-casevo-brand dark:text-casevo-link">✓</span> Statement {stRef(s)} issued{s.issuedAt ? ` ${ukDate(s.issuedAt.slice(0, 10))}` : ''}</>}>
        <dl className="m-0 grid gap-[10px] sm:grid-cols-2" aria-label="Statement summary">
          <SummaryRow label="Issued">{s.issuedAt ? ukDate(s.issuedAt.slice(0, 10)) : '—'}{s.issuedBy ? ` by ${s.issuedBy}` : ''}</SummaryRow>
          <SummaryRow label="Signed by">{s.signatoryName ?? '—'}</SummaryRow>
          {to && <SummaryRow label="Addressed to">{to}</SummaryRow>}
          {s.netDestinationDetail ? <SummaryRow label="Where the net sum goes">{s.netDestinationDetail}</SummaryRow> : null}
          <SummaryRow label="Delivered">{s.delivered ? `${DELIVERY[s.delivered.method]} ${ukDate(String(s.delivered.at).slice(0, 10))}` : 'Not sent yet'}</SummaryRow>
          <SummaryRow label="Fingerprint"><span className="tabular-nums" title={s.contentHash ?? undefined}>{shortHash(s.contentHash)}</span></SummaryRow>
          {/* (Audit fix batch 1, H2) The final bill differed and someone checked it: who, when, why. */}
          {s.billMismatchOverride && (
            <SummaryRow label={`Differs from bill ${s.billMismatchOverride.billNumber}`}>
              <span data-mismatch-override>Checked by {s.billMismatchOverride.by}{s.billMismatchOverride.at ? ` on ${ukDate(String(s.billMismatchOverride.at).slice(0, 10))}` : ''} — ‘{s.billMismatchOverride.reason}’</span>
              {s.billMismatchOverride.parts.length > 0 && (
                <span className="block text-[12.5px] text-casevo-text-soft">{s.billMismatchOverride.parts.map((p) => `${p.label}: bill ${money(p.billPence)} · statement ${money(p.statementPence)}`).join('; ')}</span>
              )}
            </SummaryRow>
          )}
        </dl>
        {s.notesReviewed && s.notesReviewed.notes.length > 0 && <p className={`${NOTE} m-0 mt-[8px]`}>Notes reviewed by {s.notesReviewed.by} on {ukDate(String(s.notesReviewed.at).slice(0, 10))}: {s.notesReviewed.notes.map((n) => n.message).join(' ')}</p>}
      </Fold>
    </section>
  )
}

// ── The client's answer (adults) ─────────────────────────────
// (Big-audit fix batch 2, M1) Moved to ./ClientAnswer (manual answers, the
// e-signed answer's later instructions, the answer history).
export { ANSWER_NEEDS_DELIVERY, ClientAnswer, ClientApprovalForm } from './ClientAnswer'

// ── The hand-off to client account (B4b) ─────────────────────
export const HandOff: React.FC<{ s: StatementSummary; representativeType?: RepresentativeType | null }> = ({ s, representativeType }) => {
  const r = s.readiness
  // Part of it is held: plain information, never green "ready" beside a dispute.
  if (r.ready && r.heldPence > 0) {
    return (
      <div role="group" aria-label="Client account hand-off" data-hand-off="held" className="rounded-[12px] bg-casevo-input px-[14px] py-[10px] text-[13px] text-casevo-forest">
        <p className="m-0 font-semibold">{r.label}, except {money(r.heldPence)} held</p>
        <dl className={`m-0 mt-[6px] ${FIGURE_GRID} text-[12.5px]`}>
          <div className="min-w-0"><dt className="text-casevo-text-soft break-words">{netSumLabel(s.netDestination, representativeType)}</dt><dd className="m-0 font-semibold tabular-nums">{money(r.netToClientPence)}</dd></div>
          <div className="min-w-0"><dt className="text-casevo-text-soft break-words">Approved deductions</dt><dd className="m-0 font-semibold tabular-nums">{money(r.approvedDeductionsPence)}</dd></div>
          <div className="min-w-0"><dt className="text-casevo-text-soft break-words">Held (disputed)</dt><dd className="m-0 font-semibold tabular-nums">{money(r.heldPence)}</dd></div>
        </dl>
        <p className={`${NOTE} m-0 mt-[4px]`}>No money moves yet — the client-account transfers come with the legal-accounts work.</p>
      </div>
    )
  }
  return (
    // (Audit fix batch 2) Green = done (ready), else neutral information.
    <ActionBox ariaLabel="Client account hand-off" tone={r.ready ? 'done' : 'info'} title={r.label} message={!r.ready && r.reason ? r.reason : undefined}>
      {r.ready && (
        <dl className={`m-0 mt-[6px] ${FIGURE_GRID} text-[12.5px]`}>
          <div className="min-w-0"><dt className="text-casevo-text-soft break-words">{netSumLabel(s.netDestination, representativeType)}</dt><dd className="m-0 font-semibold tabular-nums">{money(r.netToClientPence)}</dd></div>
          <div className="min-w-0"><dt className="text-casevo-text-soft break-words">Approved deductions</dt><dd className="m-0 font-semibold tabular-nums">{money(r.approvedDeductionsPence)}</dd></div>
          <div className="min-w-0"><dt className="text-casevo-text-soft break-words">Held (disputed)</dt><dd className="m-0 font-semibold tabular-nums">{money(r.heldPence)}</dd></div>
        </dl>
      )}
      <p className={`${NOTE} m-0 mt-[4px]`}>No money moves yet — the client-account transfers come with the legal-accounts work.</p>
    </ActionBox>
  )
}

// ── Versions ─────────────────────────────────────────────────
export const VersionList: React.FC<{ statements: StatementSummary[]; busy: string | null; onPreview: (s: StatementSummary) => void; representativeType?: RepresentativeType | null }> = ({ statements, busy, onPreview, representativeType }) => (
  <div aria-label="Statement versions">
    <h5 className="m-0 mb-[6px] text-[12px] font-semibold uppercase tracking-[0.08em] text-casevo-text-soft">Versions</h5>
    <ul className="m-0 p-0 list-none flex flex-col divide-y divide-casevo-border border border-casevo-border rounded-[10px]">
      {statements.map((s) => (
        <li key={s.id} className="flex items-center gap-[10px] px-[12px] py-[8px] text-[13px] flex-wrap" data-version={s.version}>
          <b className="tabular-nums">{stRef(s)}</b>
          <StatusChip status={s.status} />
          {s.proposed && <span className="text-[12px] text-casevo-warn">Proposed</span>}
          {s.supersededBy && <span className="text-[12px] text-casevo-warn">Superseded by v{s.supersededBy.version}</span>}
          {s.supersedes && <span className="text-[12px] text-casevo-text-soft">Replaces v{s.supersedes.version}</span>}
          <span className="text-casevo-text-soft">{s.issuedAt ? ukDate(s.issuedAt.slice(0, 10)) : ''}</span>
          <span className="flex-1" />
          <span className="tabular-nums">{netSumLabel(s.netDestination, representativeType)} {money(s.figures.netToClientPence)}</span>
          <button type="button" className={B_SECONDARY_SM} disabled={!!busy} onClick={() => onPreview(s)}>View</button>
        </li>
      ))}
    </ul>
  </div>
)
